import { useId, useMemo, type ReactNode } from 'react';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { amenityKind } from '@spacebooking/shared';
import { isRoundShape, seatPositions } from '@spacebooking/shared/geometry';
import type { FloorPlan, PlanAmenity, PlanTable } from '../lib/types';
import { classNames } from '../lib/format';
import { Pictogram } from './pictograms';
import { usePanZoom } from './usePanZoom';

export type PanZoom = ReturnType<typeof usePanZoom>;

export interface FloorMapProps {
  plan: FloorPlan;
  /** Show the colour photo layer (when the scan has one) instead of the line plan. */
  photo?: boolean;
  /** Extra CSS class for a table: drives its colour (available, booked, seated...). */
  tableClass?: (t: PlanTable) => string | undefined;
  /** Small second line under the table label (live view: "19:30 · Jones"). */
  tableCaption?: (t: PlanTable) => string | null | undefined;
  selectedIds?: string[];
  onTableClick?: (t: PlanTable) => void;
  /** Draw walking routes from this table to every sign. */
  routesFor?: PlanTable | null;
  /** Key ("kind:label") of the route to emphasise. */
  activeRoute?: string | null;
  onSignClick?: (a: PlanAmenity) => void;
  hideSigns?: boolean;
  /** Draw a light metre grid (useful in the editor). */
  grid?: boolean;
  controls?: boolean;
  className?: string;
  ariaLabel?: string;
  pz?: PanZoom;
  children?: (pz: PanZoom) => ReactNode;
  /** Rendered over the map, e.g. a hint before a time is chosen. */
  overlay?: ReactNode;
}

export function routeKey(r: { kind: string; label: string }) {
  return `${r.kind}:${r.label}`;
}

function signCategory(kind: string) {
  return amenityKind(kind)?.category ?? 'facility';
}

function asset(plan: FloorPlan, kind: string) {
  return plan.assets.find((a) => a.kind === kind) ?? null;
}

const charWidth = 0.6;

export function FloorMap(props: FloorMapProps) {
  const { plan } = props;
  const own = usePanZoom(plan.width_m, plan.height_m);
  const pz = props.pz ?? own;
  const uid = useId().replace(/:/g, '');
  const W = plan.width_m;
  const H = plan.height_m;
  const structure = asset(plan, 'structure');
  const furniture = asset(plan, 'furniture');
  const floor = asset(plan, 'floor');
  const photo = asset(plan, 'photo');
  const image = plan.backdrop === 'image' ? asset(plan, 'image') : null;
  const showPhoto = Boolean(props.photo && photo);
  const k = 1 / pz.scale; // metres per screen pixel
  const selected = useMemo(() => new Set(props.selectedIds ?? []), [props.selectedIds]);

  // Signs keep a constant on-screen size, so when zoomed out they can collide:
  // place them in priority order and shrink the ones that would overlap to icons.
  const compactSigns = useMemo(() => {
    const order = ['safety', 'accessible', 'facility', 'service', 'outdoor', 'leisure'];
    const sorted = [...plan.amenities].sort((a, b) => order.indexOf(signCategory(a.kind)) - order.indexOf(signCategory(b.kind)));
    const placed: Array<[number, number, number, number]> = [];
    const compact = new Set<string>();
    const overlaps = (r: [number, number, number, number]) =>
      placed.some((p) => r[0] < p[2] && p[0] < r[2] && r[1] < p[3] && p[1] < r[3]);
    for (const a of sorted) {
      const full = signSize(a, k, false);
      let rect: [number, number, number, number] = [a.x - full.w / 2, a.y - full.h / 2, a.x + full.w / 2, a.y + full.h / 2];
      if (overlaps(rect)) {
        compact.add(a.id);
        const small = signSize(a, k, true);
        rect = [a.x - small.w / 2, a.y - small.h / 2, a.x + small.w / 2, a.y + small.h / 2];
      }
      placed.push(rect);
    }
    return compact;
  }, [plan.amenities, k]);

  const handleTable = (t: PlanTable) => {
    if (pz.wasDrag()) return;
    props.onTableClick?.(t);
  };

  return (
    <div className={classNames('fmap', props.className)} ref={pz.ref} {...pz.handlers} aria-label={props.ariaLabel} role="group">
      <svg viewBox={pz.viewBox} width="100%" height="100%" className="fmap-svg">
        <defs>
          <pattern id={`hatch-${uid}`} patternUnits="userSpaceOnUse" width={0.14} height={0.14} patternTransform="rotate(45)">
            <rect width={0.14} height={0.14} className="fhatch-bg" />
            <line x1={0} y1={0} x2={0} y2={0.14} className="fhatch-line" strokeWidth={0.045} />
          </pattern>
          {[structure, furniture, floor].map(
            (a) =>
              a && (
                <mask key={a.kind} id={`m-${a.kind}-${uid}`} maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
                  <image href={a.url} x={0} y={0} width={W} height={H} preserveAspectRatio="none" />
                </mask>
              ),
          )}
        </defs>

        <rect x={-W} y={-H} width={W * 3} height={H * 3} className="fpaper" />
        {floor && !showPhoto ? (
          <rect x={0} y={0} width={W} height={H} className="ffloor" mask={`url(#m-floor-${uid})`} />
        ) : !showPhoto && !image ? (
          <rect x={0} y={0} width={W} height={H} className="ffloor ffloor-plain" rx={0.15} />
        ) : null}
        {props.grid && <Grid w={W} h={H} />}
        {showPhoto && photo && (
          <>
            <image href={photo.url} x={0} y={0} width={W} height={H} preserveAspectRatio="none" className="fphoto" />
            <rect x={0} y={0} width={W} height={H} className="fphoto-scrim" />
          </>
        )}
        {image && <image href={image.url} x={0} y={0} width={W} height={H} preserveAspectRatio="none" className="fimage" />}
        {furniture && !showPhoto && <rect x={0} y={0} width={W} height={H} className="ffurniture" mask={`url(#m-furniture-${uid})`} />}
        {structure && <rect x={0} y={0} width={W} height={H} className={classNames('fstructure', showPhoto && 'on-photo')} mask={`url(#m-structure-${uid})`} />}

        <g className="fwalls">
          {plan.walls.map((w, i) => (
            <Wall key={i} wall={w} />
          ))}
        </g>

        {props.routesFor && (
          <g className="froutes">
            {props.routesFor.wayfinding.map((r) => {
              const active = props.activeRoute === routeKey(r);
              return (
                <polyline
                  key={routeKey(r) + r.x}
                  points={r.path.map((p) => p.join(',')).join(' ')}
                  className={classNames('froute', `froute-${signCategory(r.kind)}`, active && 'is-active', !r.reachable && 'is-straight')}
                />
              );
            })}
          </g>
        )}

        <g className="ftables">
          {plan.tables.map((t) => (
            <TableShape
              key={t.id}
              table={t}
              className={props.tableClass?.(t)}
              caption={props.tableCaption?.(t) ?? null}
              selected={selected.has(t.id)}
              hatchId={`hatch-${uid}`}
              interactive={Boolean(props.onTableClick)}
              onClick={() => handleTable(t)}
              k={k}
            />
          ))}
        </g>

        {!props.hideSigns && (
          <g className="fsigns">
            {plan.amenities.map((a) => (
              <Sign
                key={a.id}
                sign={a}
                k={k}
                compact={compactSigns.has(a.id)}
                onClick={props.onSignClick ? () => !pz.wasDrag() && props.onSignClick!(a) : undefined}
              />
            ))}
          </g>
        )}
        {props.children?.(pz)}
      </svg>
      {props.overlay}
      {props.controls !== false && (
        <div className="fmap-controls" onPointerDown={(e) => e.stopPropagation()}>
          <button type="button" className="btn btn-icon btn-sm" onClick={() => pz.zoomBy(1.35)} aria-label="Zoom in">
            <Plus />
          </button>
          <button type="button" className="btn btn-icon btn-sm" onClick={() => pz.zoomBy(1 / 1.35)} aria-label="Zoom out">
            <Minus />
          </button>
          <button type="button" className="btn btn-icon btn-sm" onClick={() => pz.fit()} aria-label="Fit plan to screen">
            <Maximize2 />
          </button>
        </div>
      )}
    </div>
  );
}

function Grid({ w, h }: { w: number; h: number }) {
  const lines = [];
  for (let x = 1; x < w; x++) lines.push(<line key={`x${x}`} x1={x} y1={0} x2={x} y2={h} className={x % 5 === 0 ? 'fgrid-major' : 'fgrid'} />);
  for (let y = 1; y < h; y++) lines.push(<line key={`y${y}`} x1={0} y1={y} x2={w} y2={y} className={y % 5 === 0 ? 'fgrid-major' : 'fgrid'} />);
  return <g>{lines}</g>;
}

function Wall({ wall }: { wall: FloorPlan['walls'][number] }) {
  const { x1, y1, x2, y2, kind } = wall;
  if (kind === 'window') {
    const len = Math.hypot(x2 - x1, y2 - y1) || 1;
    const nx = (-(y2 - y1) / len) * 0.06;
    const ny = ((x2 - x1) / len) * 0.06;
    return (
      <g className="fwall-window">
        <line x1={x1 + nx} y1={y1 + ny} x2={x2 + nx} y2={y2 + ny} />
        <line x1={x1 - nx} y1={y1 - ny} x2={x2 - nx} y2={y2 - ny} />
      </g>
    );
  }
  if (kind === 'door') {
    const len = Math.hypot(x2 - x1, y2 - y1);
    const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
    return (
      <g className="fwall-door" transform={`translate(${x1} ${y1}) rotate(${angle})`}>
        <line x1={0} y1={0} x2={0} y2={len * 0.92} />
        <path d={`M ${len} 0 A ${len} ${len} 0 0 1 0 ${len}`} />
      </g>
    );
  }
  return <line x1={x1} y1={y1} x2={x2} y2={y2} className={`fwall fwall-${kind}`} />;
}

function TableShape(props: {
  table: PlanTable;
  className?: string;
  caption: string | null;
  selected: boolean;
  hatchId: string;
  interactive: boolean;
  onClick: () => void;
  k: number;
}) {
  const { table: t, k } = props;
  const seats = useMemo(() => seatPositions(t), [t.shape, t.width, t.height, t.max_covers]);
  const w = t.width;
  const h = t.height;
  const round = isRoundShape(t.shape);
  const label = Math.max(0.22, Math.min(0.42, Math.min(w, h) * 0.42));
  const booked = props.className?.includes('t-booked');
  return (
    <g
      className={classNames('ftable', props.className, props.selected && 't-selected', props.interactive && 'is-interactive')}
      transform={`translate(${t.x} ${t.y}) rotate(${t.rotation})`}
      onClick={props.interactive ? props.onClick : undefined}
      onKeyDown={
        props.interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.onClick();
              }
            }
          : undefined
      }
      tabIndex={props.interactive ? 0 : undefined}
      role={props.interactive ? 'button' : undefined}
      aria-label={`Table ${t.label}, seats ${t.min_covers === t.max_covers ? t.max_covers : `${t.min_covers} to ${t.max_covers}`}`}
      aria-pressed={props.interactive ? props.selected : undefined}
    >
      {props.selected && (round ? <ellipse rx={w / 2 + 0.62} ry={h / 2 + 0.62} className="fhalo" /> : <rect x={-w / 2 - 0.6} y={-h / 2 - 0.6} width={w + 1.2} height={h + 1.2} rx={0.35} className="fhalo" />)}
      {t.shape === 'booth' ? (
        <>
          <rect x={-w / 2 - 0.08} y={-h / 2 - 0.62} width={w + 0.16} height={0.5} rx={0.12} className="fbench" />
          <rect x={-w / 2 - 0.08} y={h / 2 + 0.12} width={w + 0.16} height={0.5} rx={0.12} className="fbench" />
        </>
      ) : (
        seats.map((s, i) => (
          <rect
            key={i}
            x={s.x - 0.18}
            y={s.y - 0.18}
            width={0.36}
            height={0.36}
            rx={t.shape === 'high' ? 0.18 : 0.07}
            className="fchair"
            transform={`rotate(${(s.angle * 180) / Math.PI + 90} ${s.x} ${s.y})`}
          />
        ))
      )}
      {round ? (
        <ellipse rx={w / 2} ry={h / 2} className="ftop" />
      ) : (
        <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={0.06} className="ftop" />
      )}
      {booked && (round ? <ellipse rx={w / 2} ry={h / 2} fill={`url(#${props.hatchId})`} className="fhatch" /> : <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={0.06} fill={`url(#${props.hatchId})`} className="fhatch" />)}
      <g transform={`rotate(${-t.rotation})`}>
        <text className="flabel" y={props.caption ? -label * 0.15 : 0} fontSize={label} dominantBaseline="central" textAnchor="middle">
          {t.label}
        </text>
        {props.caption && (
          <text className="fcaption" y={label * 0.72} fontSize={Math.max(10 * k, Math.min(label * 0.62, 13 * k))} dominantBaseline="central" textAnchor="middle">
            {props.caption}
          </text>
        )}
      </g>
    </g>
  );
}

export function signLabel(a: { kind: string; label: string | null }) {
  return a.label || amenityKind(a.kind)?.label || a.kind;
}

function signSize(sign: PlanAmenity, k: number, compact: boolean) {
  const iconOnly = compact || k > 1 / 14;
  const icon = 18 * k;
  const pad = 6 * k;
  const font = 12 * k;
  const textW = iconOnly ? 0 : signLabel(sign).length * font * charWidth;
  const arrow = sign.pointer_deg !== null && sign.pointer_deg !== undefined;
  const arrowW = arrow && !iconOnly ? 14 * k : 0;
  const w = pad + icon + (textW ? pad * 0.9 + textW : 0) + (arrowW ? pad * 0.6 + arrowW : 0) + pad;
  return { w, h: icon + pad * 1.6, icon, pad, font, iconOnly, arrowW, arrow };
}

/** A wayfinding sign drawn at a constant on-screen size, like a real sign plate. */
function Sign({ sign, k, compact, onClick }: { sign: PlanAmenity; k: number; compact: boolean; onClick?: () => void }) {
  const info = amenityKind(sign.kind);
  const category = info?.category ?? 'facility';
  const text = signLabel(sign);
  const { w: width, h: height, icon, pad, font, iconOnly: zoomedOut, arrowW, arrow } = signSize(sign, k, compact);
  const x0 = -width / 2;
  return (
    <g
      className={classNames('fsign', `fsign-${category}`, onClick && 'is-interactive')}
      transform={`translate(${sign.x} ${sign.y})`}
      onClick={onClick}
      role="img"
      aria-label={text}
    >
      <title>{text}</title>
      <rect x={x0} y={-height / 2} width={width} height={height} rx={4 * k} className="fsign-plate" />
      <Pictogram icon={info?.icon ?? 'info'} x={x0 + pad} y={-icon / 2} width={icon} height={icon} size={icon} className="fsign-icon" strokeWidth={2.2} />
      {!zoomedOut && (
        <text x={x0 + pad + icon + pad * 0.9} y={0} fontSize={font} dominantBaseline="central" className="fsign-text">
          {text}
        </text>
      )}
      {arrow && !zoomedOut && (
        <g transform={`translate(${x0 + width - pad - arrowW / 2} 0) rotate(${sign.pointer_deg})`}>
          <path
            d={`M ${-arrowW * 0.42} 0 H ${arrowW * 0.42} M ${arrowW * 0.08} ${-arrowW * 0.34} L ${arrowW * 0.44} 0 L ${arrowW * 0.08} ${arrowW * 0.34}`}
            className="fsign-arrow"
            strokeWidth={2 * k}
          />
        </g>
      )}
    </g>
  );
}
